<template>
  <Modal :value="value" @input="closeModal" width="lg">
    <div slot="header">
      Consulta avulsa de veículos
    </div>

    <div class="col-md-12">
      <input
          class="input form-control mb-2"
          @keypress.enter="find()"
          v-model="query.generic"
          placeholder="Pesquisar por Placa"
      >
      <search-icon @click="find()" class="icon icon-within"/>
    </div>
    <div class="col-md-12" v-for="vehicle in vehicles" :key="vehicle.id">
      <div class="accordion">
        <div class="accordion-header" @click="toggleAccordion(vehicle.id)">
          <h4>{{ vehicle.plate }}</h4>
          <div>
            <span v-if="getServiceCount(vehicle) > 0">{{ getServiceCount(vehicle) }} órgão(s) selecionado(s)</span>
            <chevron-down-icon class="fa-angle-right" :class="{'rotate-90': activeAccordion === vehicle.id }"/>
          </div>
        </div>
        <div v-if="activeAccordion === vehicle.id" class="accordion-body">
          <div class="form-group col-md-6">
            <div class="form-check" v-for="service in getEnableService(vehicle.services)" :key="service.name">
              <input type="checkbox" :id="getServiceCheckboxId(vehicle.id, service.name)" v-model="selectedServices[`${vehicle.id}#${service.name}`]" class="form-check-input">
              <label :for="getServiceCheckboxId(vehicle.id, service.name)" class="form-check-label">
                {{ service.friendlyName || service.name }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
    <hr>
    <div v-if="serviceSelected" class="summary-section">
      <small class="bold-text">Resumo de consultas: </small>
      <p v-for="vehicle in cacheVehicles" :key="vehicle.id">
        <small v-if="vehicle.countService > 0">{{ vehicle.plate }} - {{ vehicle.countService }} órgão(s) selecionado(s)</small>
      </p>
    </div>
    <div v-if="showDateRange" class="col-md-12 date-range-section">
      <small class="bold-text">Período (Prefeitura SP):</small>
      <div class="row">
        <div class="form-group col-md-6">
          <label>Data inicial</label>
          <input type="date" v-model="startDate" class="input form-control">
        </div>
        <div class="form-group col-md-6">
          <label>Data final</label>
          <input type="date" v-model="endDate" class="input form-control">
        </div>
      </div>
    </div>
    <button class="btn btn-primary float-right" :disabled="!serviceSelected || accountIsClosed"
            @click="sendConsultRequests">
      Consultar
    </button>
  </Modal>
</template>

<script>
import GetAccount from '@/mixins/GetAccount'
import GetUser from '@/mixins/GetUser'
import Modal from '@/components/shares/specialTag/Modal'
import ModelMapper from '@/mixins/ModelMapper'

import { Converter } from '@/models/converter'
import { ChevronDownIcon, SearchIcon } from 'vue-feather-icons'
import { VEHICLE_STATUS } from '@/utils/vehicleStatus'

const toCamelcase = string => {
  return new Converter({
    converterName: 'camelcase',
    data: string
  }).perform()
}
const toSnakecase = string => {
  return new Converter({
    converterName: 'snakecase',
    data: string
  }).perform()
}

const PREFEITURA_SP_SCRAPER = 'autos_prefeitura_sao_paulo_sp'

export default {
  components: {
    Modal,
    ChevronDownIcon,
    SearchIcon
  },
  created() {
    this.$set(this.query, 'entityStatusId', VEHICLE_STATUS.ENABLED)
  },
  computed: {
    serviceSelected() {
      return Object.values(this.selectedServices).some(value => value === true)
    },
    vehicles() {
      return this.getModels || []
    },
    prefeituraSpSelected() {
      return Object.entries(this.selectedServices)
          .some(([key, value]) => value && key.endsWith(`#${PREFEITURA_SP_SCRAPER}`))
    },
    showDateRange() {
      return this.isSuperUser && this.prefeituraSpSelected
    },
  },
  methods: {
    closeModal() {
      this.$emit('input', false)
    },
    fetchScrapingServices() {
      const params = {
        meta: false,
        per_page: 10000,
        query: {
          enable: true,
          app_name: this.appName
        }
      }

      return this.axios.get('maestro/scraping_services', { params })
        .then(response => this.scrapingServices = toCamelcase(response.data))
        .then(() => this.scrapingServiceLoaded = true)
        .catch(e => this.$notify.addError(e))
    },
    find() {
      this.asyncTemplateSelect()
    },
    getServiceCount(vehicle) {
      const keys = Object.keys(vehicle.services).map(key => `${vehicle.id}#${this.$parse.toSnakecase(key)}`)

      let acumulator = 0
      keys.forEach(key => {
        if (this.selectedServices[key])
          acumulator++
      })
      return acumulator
    },
    getEnableService(vehicleServices) {
      const services = []

      Object.entries(vehicleServices).forEach(([serviceName, config]) => {
        const service = this.scrapingServices.find(s => config.enable && s.name === toSnakecase(serviceName))

        if (service)
          services.push(service)
      })

      return services
    },
    getServiceCheckboxId(vehicleId, serviceName) {
      return `${vehicleId}-${serviceName}-checkbox`
    },
    sendConsultRequests() {
      if (!confirm('Tem certeza de que deseja agendar as consultas?'))
        return

      let delay = 0

      const selectedServices = Object.entries(this.selectedServices).map(([key, value]) => {
        const [vehicleId, serviceName] = key.split('#')
        const vehicle = this.cacheVehicles[parseInt(vehicleId)]
        return {
          vehicleId: parseInt(vehicleId),
          serviceName,
          plate: vehicle?.plate,
          enable: value,
          startDate: this.startDate,
          endDate: this.endDate
        }
      }).filter(obj => obj.enable)

      selectedServices.forEach(service => {
        setTimeout(() => this.sendConsultByService(service), delay)
        delay += 800
      })

      this.closeModal()
    },
    sendConsultByService(service) {
      const data = {
        scraper_name: service.serviceName,
        vehicle_id: service.vehicleId,
      }

      if (this.isSuperUser && service.serviceName === PREFEITURA_SP_SCRAPER && (service.startDate || service.endDate)) {
        data.start_date = service.startDate || null
        data.end_date = service.endDate || null
      }

      this.openGlobalLoad()
      this.axios
        .post('maestro/consults', data)
        .then(() => this.$notify.add(`A consulta ${service.serviceName} para o veículo placa ${service.plate} foi agendada`))
        .catch(e => {
          if (e.errors && e.errors[0].includes('credencial')) {
            this.$notify.addError(`Credenciais não encontradas para o veículo placa ${service.plate} no serviço ${service.serviceName}`)
          } else {
            this.$notify.addError(e)
          }
        })
        .finally(() => this.closeGlobalLoad())
    },
    toggleAccordion(id) {
      if (this.activeAccordion === id)
        this.activeAccordion = null
      else
        this.activeAccordion = id
    },
    updateCacheVehicles() {
      this.vehicles.forEach(vehicle => {
        this.cacheVehicles[vehicle.id] = {
          plate: vehicle.plate,
          countService: Object.entries(this.selectedServices)
              .filter(([key, value]) => value && key.startsWith(`${vehicle.id}#`))
              .length
        }
      })
    },
    clearSelections() {
      this.selectedServices = {}
      this.cacheVehicles = {}
      this.query.generic = ''
      this.activeAccordion = null
      this.models = []
      this.startDate = ''
      this.endDate = ''
    }
  },
  mixins: [GetAccount, GetUser, ModelMapper],
  name: 'VehicleRequestModal',
  data: () => ({
    cacheVehicles: {},
    activeAccordion: null,
    selectedServices: {},
    nameModel: 'Vehicle',
    scrapingServices: [],
    scrapingServiceLoaded: false,
    startDate: '',
    endDate: ''
  }),
  props: {
    value: { default: false, type: Boolean },
    appName: { required: true, type: String },
  },
  watch: {
    vehicles() {
      this.updateCacheVehicles()
    },
    selectedServices: {
      handler() {
        this.updateCacheVehicles()
      },
      deep: true
    },
    value(newValue) {
      if (newValue && !this.scrapingServiceLoaded)
        this.fetchScrapingServices()

      if (!newValue)
        this.clearSelections()
    }
  }
}
</script>

<style scoped>
.accordion {
  border: 1px solid var(--table-border);
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;
}

.accordion-header {
  padding: 1rem;
  background-color: var(--background-tab);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  color: var(--primary-text-color);
}

.accordion-header h4 {
  color: var(--primary-text-color);
  margin: 0;
}

.accordion-header span {
  color: var(--secondary-text-color);
}

.accordion-body {
  padding: 1rem;
  background-color: var(--white-bg);
}

.summary-section {
  color: var(--primary-text-color);
}

.summary-section .bold-text {
  color: var(--primary-text-color);
  font-weight: bold;
}

.summary-section p {
  color: var(--secondary-text-color);
}
</style>
